import appConfig from '../config'
import { unleashBeVersion } from '@utils/setup-application/createUnleash'
import type {
  AxiosRequestHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { client, uninterceptedClient } from 'src/services/client'
import LOCAL_STORAGE_KEYS from '@constants/localStorageKeys'

export const getAccessToken = (): string | undefined => {
  return localStorage.getItem(LOCAL_STORAGE_KEYS.token) ?? undefined
}

export const updateAuthToken = (authToken: string) => {
  const setAccessTokenInLocalStorage = (token: string): void => {
    localStorage.setItem(LOCAL_STORAGE_KEYS.token, token)
  }
  const updateTokensInHttpClients = (token: string): void => {
    client.defaults.headers.common.Authorization = token
    uninterceptedClient.defaults.headers.common.Authorization = token
  }

  setAccessTokenInLocalStorage(authToken)
  updateTokensInHttpClients(authToken)
}

/**
 * This function should be pure
 * TODO use this anywhere in the app and test it, then remove this comment.
 * IMPORTANT: This function must remain pure so that it is testable without mocks.
 * @param fetchArg
 * @param options - do not need to be passed in normal usage, only for testing
 * @returns a fetch function that adds and updates authorization headers
 */
export const withAuthorization =
  (
    fetchArg: typeof fetch,
    options: {
      getToken: () => string | undefined
      setToken: (token: string) => void
      getBackendVersion: () => string
    } = {
      getToken: getAccessToken,
      setToken: updateAuthToken,
      getBackendVersion: getBackendVersion,
    },
  ): typeof fetch =>
  async (input, init) => {
    const { getToken, setToken, getBackendVersion } = options
    const currentAccessToken = getToken()
    const newInit: typeof init = {
      ...init,
      headers: {
        'SB-BE-Version': getBackendVersion(),
        ...(currentAccessToken ? { Authorization: currentAccessToken } : {}),
        ...(init?.headers || {}),
      },
    }

    const response = await fetchArg(input, newInit)

    // Update token if present in response headers
    const nextAccessToken = response.headers.get('token') ?? undefined
    if (getToken() && nextAccessToken !== undefined) {
      setToken(nextAccessToken)
    }

    return response
  }

export function successInterceptor(response: AxiosResponse): AxiosResponse {
  if (response.headers?.token) {
    const authToken = response.headers.token as unknown

    if (getAccessToken() && typeof authToken === 'string') {
      updateAuthToken(authToken)
    }
  }
  return response
}

export function errorInterceptor(error: {
  response: { status: unknown; config: unknown; data: unknown }
}): Promise<never> {
  return Promise.reject(error)
}

type AxiosRequestConfigWithHeaders = InternalAxiosRequestConfig<{
  headers: AxiosRequestHeaders
  region?: string
}>

const getBackendVersion = (): string =>
  process.env.VUE_APP_BACKEND_VERSION || unleashBeVersion() || 'latest'

export function beforeRequestInterceptor(
  config: AxiosRequestConfigWithHeaders,
): AxiosRequestConfigWithHeaders {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const updatedConfig = {
    ...config,
    headers: {
      ...config.headers,
      'SB-BE-Version': getBackendVersion(),
    },
  } as unknown as AxiosRequestConfigWithHeaders

  const shouldUpdateBaseUrl = () => config.region !== 'cn-north-1'
  if (config.region && shouldUpdateBaseUrl()) {
    updatedConfig.baseURL = appConfig.regionSpecificApi(config.region)
  }

  return updatedConfig
}

export function requestErrorInterceptor(error: unknown): Promise<never> {
  return Promise.reject(error)
}
